import { routes } from "@/global/routes";
import { Box, Link, Stack, Typography } from "@mui/material";
import { HiOutlineTicket } from "react-icons/hi";
import { MdOutlineEventSeat, MdOutlinedFlag } from "react-icons/md";
import { TbShirtSport } from "react-icons/tb";
import { createUrlFromNextRouter } from "@/global/utils/utils";
import teamConfig from "./dbacks_gift";
import Head from "next/head";
import { TeamConfig } from "@/global/hooks/useTeamConfig";
import constants from "@/global/config/constants";
import { MLBAdobeScriptHeader } from "@/global/common/mlb-common/MLBAdobeScriptHeader";

export const purchaseGiftCardLanguages = {
  navMenueLinkText: "Share a Gift",
  pageTitle: "Give the gift of Diamondbacks Baseball",
  mainCardTitle: "Share a Gift",
  mainCardDescription:
    "Share the magic of Diamondbacks baseball tickets with a friend or loved one. Upon purchase of the gift, a unique code will be generated for the intended recipient to redeem. Once redeemed, the gift is non-refundable.",
  amountTitle: "Gift Amount",
  recipientTitle: "Recipient",
  recipientEnabledDesc:
    "The Recipient will be sent an email with the unique code for them to redeem their gift. Once redeemed, the recipient will receive an account credit that they can use to purchase tickets for all Arizona Diamondbacks games at Chase Field.",
  recipientDisabledDesc:
    "If an intended recipient is not specified, once purchased, you will have to forward the unique code to the recipient. Upon your recipient receiving their gift, they will be prompted to enter their unique code to redeem the gift. Once redeemed, the recipient will receive an account credit that they use to purchase tickets for all Regular Season Arizona Diamondbacks games at Chase Field.",
};

export const redeemGiftCardLanguages = {
  navMenueLinkText: "Redeem Gift",
  pageTitle: "Give the gift of Diamondbacks Baseball",
  mainCardTitle: "Redeem Gift",
  mainCardLoggedInDesc:
    "Please input the case sensitive code provided to redeem the value of the gift to attend Arizona Diamondbacks games at Chase Field.",
  mainCardLoggedOutDesc:
    "Please input the case sensitive unique code provided to redeem the value of the gift card to the Ticketmaster.",
  successDesc: "has been added to to your account!",
};

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "Choose Your Games or Applicable Pack",
    description: "Select the pack you wish to purchase to choose your games.",
  },
  {
    icon: <MdOutlineEventSeat />,
    title: "Login to Account Manager",
    description:
      " Click Continue and connect your Arizona Diamondbacks account using our secure Ticketmaster login.",
  },
  {
    icon: <TbShirtSport />,
    title: "Select Your Seats",
    description: "Select the seats you wish to purchase for each game.",
  },

  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description:
      "Tickets will be available in your Account Manager within 24 hours of redemption. Reach out to Live Fan Support for any questions.",
  },
];

export const faqs = [
  {
    title: "How does the Arizona Diamondbacks Flex program work?",
    description:
      "The Arizona Diamondbacks program allows fans to select any seat to select regular season games. Simply log into your Arizona Diamondbacks Account Manager and select games.",
  },
  {
    title: "Do I have to select all my games now?",
    description: "Yes, you must select all games at the time of purchase.",
  },
  {
    title: "Can I choose seats throughout the venue?",
    description: "Yes, you can choose from all current available inventory.",
  },
  {
    title: "Where are event tickets delivered?",
    description:
      "Event tickets are delivered directly within your Arizona Diamondbacks Account Manager.",
  },
];

export function TermsAndCondition() {
  return (
    <>
      In connection with your purchase, you will receive ticket-related
      commercial emails from{" "}
      <Link
        href={"http://dbacks.com/"}
        target={"_blank"}
        sx={{
          color: "primary.main",
          textDecoration: "none",
          fontWeight: 500,
        }}
      >
        dbacks.com
      </Link>
      . You may unsubscribe at any time. By checking this box you agree to
      receive additional commercial emails from{" "}
      <Link
        href={"http://dbacks.com/"}
        target={"_blank"}
        sx={{
          color: "primary.main",
          textDecoration: "none",
          fontWeight: 500,
        }}
      >
        dbacks.com
      </Link>{" "}
      and{" "}
      <Link
        href={"http://mlb.com/"}
        target={"_blank"}
        sx={{
          color: "primary.main",
          textDecoration: "none",
          fontWeight: 500,
        }}
      >
        MLB.com
      </Link>
      . The terms and conditions of your ticket and policies applicable to its
      use are updated to address circumstances related to COVID-19 and health
      and safety. By clicking the Purchase Tickets button, you agree to accept
      all{" "}
      <Link
        href={createUrlFromNextRouter(routes.termsAndConditions, teamConfig)}
        target={"_blank"}
        sx={{
          color: "primary.main",
          textDecoration: "none",
          fontWeight: 500,
        }}
      >
        Terms and Conditions
      </Link>
      . Further, due to the COVID-19 pandemic, scheduling may be impacted; games
      may be canceled, rescheduled, or postponed or games may be held with
      limited capacity. Upon completing your purchase by selecting Purchase
      Tickets you acknowledge and agree that your credit card will be charged.
      By clicking “Purchase Tickets”, you agree to the{" "}
      <Link
        href={
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy"
        }
        target={"_blank"}
        sx={{
          color: "primary.main",
          textDecoration: "none",
          fontWeight: 500,
        }}
      >
        MLB Privacy Policy
      </Link>
      ,{" "}
      <Link
        href={
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use"
        }
        target={"_blank"}
        sx={{
          color: "primary.main",
          textDecoration: "none",
          fontWeight: 500,
        }}
      >
        Terms of Use
      </Link>{" "}
      and to receive your ticket(s) via an SMS text message to the mobile #
      associated with your Diamondbacks account. Message and data rates may
      apply Tickets will be made available in the MLB Ballpark mobile
      application only.{" "}
      <Link
        href={"http://mlb.com/"}
        target={"_blank"}
        sx={{
          color: "primary.main",
          textDecoration: "none",
          fontWeight: 500,
        }}
      >
        MLB.com
      </Link>{" "}
      and supported mobile device required.
    </>
  );
}

export const DBacksStandaloneTermsAndConditions = () => (
  <Box pb={2}>
    <Typography fontSize={"2rem"} fontWeight={800}>
      Gift Card Terms and Conditions
    </Typography>
    <Typography fontSize="1rem" fontWeight={"bold"} mt={2}>
      AS OF FEBRUARY 2024
    </Typography>
    <Typography mt={3}>
      1. <b>Terms and Conditions.</b> The following Gift Card Terms and
      Conditions (“Agreement”) describe the terms and conditions that apply to
      the use of gift cards that are sold or issued by Issuer (defined below) in
      the United States as described herein (each gift card individually, “Gift
      Card” or “Card” and collectively, “Gift Cards” or “Cards”). This Agreement
      is between you, the holder of a Gift Card (the “Cardholder” or “you”), and
      AZPB Limited Partnership, dba Arizona Diamondbacks (the “Issuer”). By
      purchasing, accepting or using your Card, you expressly agree to be bound
      by this Agreement. If you do not agree with this Agreement, do not
      purchase, use, or accept the Card.{" "}
      <b>
        IMPORTANT: This Agreement includes resolution of disputes by binding
        arbitration instead of in court. FURTHER, THESE TERMS CONTAIN A CLASS
        ACTION WAIVER THAT REQUIRES THAT ALL CLAIMS BE BROUGHT INDIVIDUALLY, NOT
        AS A CLASS REPRESENTATIVE OR CLASS MEMBER. SEE SECTION 15 FOR MORE
        DETAILS.
      </b>
    </Typography>
    <Typography mt={3}>
      2. <b>Overview.</b> Issuer is a Major League Baseball team. Issuer has set
      up an account manager platform for you to sign up and manage your personal
      My D-backs Account to redeem account credit (“Account Credit”) towards
      eligible Arizona Diamondbacks home games at Chase Field (“My D-backs
      Account”).
    </Typography>
    <Typography mt={3}>
      3. <b>About Gift Cards.</b> The Gift Cards are issued by the Issuer. The
      Issuer is responsible for the operation and maintenance of the Gift Cards
      program. As between the Cardholders and the Issuer, the Issuer is the sole
      legal obligor to the Cardholders; provided, however, that the Issuer may
      assign its obligations with respect to the Gift Cards at any time to the
      extent permitted by applicable law, in which case such assignee shall
      become the sole legal obligor and you hereby knowingly release the Issuer
      from any and all liabilities and claims of any kind, nature or character
      whatsoever arising in connection with the Gift Cards. The Gift Cards are
      denominated in U.S. Dollars. The Gift Cards are neither debit cards nor
      credit cards and cannot be used as such.
    </Typography>
    <Typography mt={3}>
      4. <b>Issuance of Gift Cards.</b> Gift Cards may be purchased in your My
      D-backs Account. Gift Cards are not valid if purchased from any other
      seller. Gift Cards are redeemable Account Credit only through your My
      D-backs Account for the purchase of tickets for eligible Arizona
      Diamondbacks home games. Exclusions apply. Gift Cards may not be redeemed
      toward the purchase of tickets on any other ticketing platform for Arizona
      Diamondbacks games, nor used to purchase tickets for any other game or
      event at Chase Field. Further, Gift Cards may not be used to purchase
      other Gift Cards.
    </Typography>
    <Stack>
      <Typography mt={3}>
        5. <b>Redemption of Gift Cards.</b>
      </Typography>
      <Stack pl={2} gap={1}>
        <Typography>
          a. Eligible Gift Card Recipients will receive an email notifying them
          that they have received a Gift Card (“Gift Card Email”). To accept and
          redeem the Gift Card, you should click on the redemption link in the
          Gift Card Email to visit the Gift Card redemption website and input
          the case sensitive redemption code, your name, and other information.
          If you do not have an existing My D-backs Account, you will need to
          create a My D-backs Account. Any Eligible Gift Card Recipients who
          already have an existing My D-backs Account may redeem Gift Cards for
          Account Credit by logging on to their My D-backs Account, where they
          will have the opportunity to redeem the Gift Card for Account Credits.
          “Eligible Gift Card Recipient” means any individual resident of the
          United States or any U.S. territory, aged eighteen (18) or older (or
          the age of majority in your state or territory of residence), who does
          not have a My D- backs Account or who has a Local My D-backs Account.
          By using the My D-backs Account or calling an Issuer service agent,
          the Eligible Gift Card Recipient agrees with: (i) Arizona Diamondbacks
          Regular Season and Postseason Ticket{" "}
          <Link
            href="https://www.mlb.com/dbacks/tickets/terms-conditions"
            target={"_blank"}
            sx={{
              color: "primary.main",
              textDecoration: "none",
              fontWeight: 500,
            }}
          >
            Terms and Conditions
          </Link>{" "}
          for purchases of single tickets; and (ii) acknowledges the collection
          and use of your data in accordance with the{" "}
          <Link
            href="https://www.mlb.com/official-information/privacy-policy"
            target={"_blank"}
            sx={{
              color: "primary.main",
              textDecoration: "none",
              fontWeight: 500,
            }}
          >
            MLB Privacy Policy
          </Link>
          , all of which are incorporated by reference herein. If there is a
          conflict between the Arizona Diamondbacks Terms and Conditions and the
          MLB Privacy Policy on the one hand, and this Agreement on the other,
          the terms and conditions of this Agreement shall govern.
        </Typography>
        <Typography>
          b. If a Cardholder redeems a Gift Card, the Cardholder’s My D-backs
          Account will be credited Account Credits that are equal in value to
          the Gift Card. Account Credits may be applied to any purchases made on
          the My D-backs Account and any applicable fees and taxes until the
          Account Credits are fully exhausted. In the event that the remaining
          Account Credits corresponding with a Gift Card are insufficient to
          cover the cost of a purchase, an additional method of payment for the
          remaining balance will be due.
        </Typography>
        <Typography>
          c. Once Gift Cards are redeemed for Account Credits, the Account
          Credits are subject to the terms and conditions for each Arizona
          Diamondbacks game that are posted on the dedicated web page accessible
          from{" "}
          <Link
            href="https://www.mlb.com/dbacks/tickets/terms-conditions"
            target={"_blank"}
            sx={{
              color: "primary.main",
              textDecoration: "none",
              fontWeight: 500,
            }}
          >
            https://www.mlb.com/dbacks/tickets/terms-conditions
          </Link>{" "}
          for your Arizona Diamondbacks game, and any materials and
          communications sent to you via any format (such materials and
          communications are collectively defined as “Local Terms”).
        </Typography>
      </Stack>
    </Stack>
    <Typography mt={3}>
      6. <b>No Cash Redemption.</b> The Gift Cards have no cash value and may
      not be redeemed for cash except as required by applicable law. Promotional
      offers, discounts and store credits cannot be applied to the purchase of
      Gift Cards, and purchases of Gift Cards cannot be used to satisfy
      promotional thresholds. Treat Gift Card like cash; the Issuer is not
      responsible for lost or stolen Gift Cards.
    </Typography>
    <Typography mt={3}>
      7. <b>Activation.</b> A Gift Card is not valid for use until it has been
      activated through the process described in Section 5.
    </Typography>
    <Typography mt={3}>
      8. <b>Non-Payment.</b> The Issuer reserves the right to refuse to honor
      any Gift Card in the event of a disputed credit card charge, bounced check
      or other failure of consideration.
    </Typography>
    <Typography mt={3}>
      9. <b>Gift Card Balance Inquiry.</b> For Gift Card balance inquiries or
      any other questions, please log in to your My D-backs Account or call
      1-805-265-0042 for SeasonShare support. The balance obtained by a
      Cardholder when inquiring over the telephone or online is an estimate
      only. In most cases, the balance is adjusted immediately when a Cardholder
      makes a purchase, but there may be occasions when the balance adjustment
      is delayed.
    </Typography>
    <Typography mt={3}>
      10. <b>No Expiration; No Fees.</b> The Gift Cards do not expire. No
      inactivity, dormancy or service fees apply to any Gift Cards. Once the
      Gift Cards are redeemed for Account Credits, the terms and conditions of
      the Account Credits, including expiration date, if any, is subject to the
      Local Terms.
    </Typography>
    <Typography mt={3}>
      11. <b>Refunds and Returns.</b> The Gift Cards are not refundable except
      as required by applicable law.
    </Typography>
    <Typography mt={3}>
      12. <b>No Resale.</b> The Gift Cards may not be resold without the
      Issuer’s express written authorization. A Gift Card is not valid and will
      not be honored, and the Issuer will not be liable for the value of the
      Card, if the Gift Card is obtained from an unauthorized seller or
      reseller, including through any Internet auction site.
    </Typography>
    <Typography mt={3}>
      13. <b>Lost, Damaged or Stolen Gift Cards.</b> A lost, damaged or stolen
      Gift Card [OR misdirected Gift Card Email] may be replaced only if the
      Cardholder provides the original proof of purchase receipt and the Gift
      Card’s redemption code. To request a replacement Gift Card, please email{" "}
      <Link
        href="mailto:support@seasonshare.com"
        target={"_blank"}
        sx={{
          color: "primary.main",
          textDecoration: "none",
          fontWeight: 500,
        }}
      >
        support@seasonshare.com
      </Link>
      . The Issuer is not responsible for unauthorized use of any Gift Card.
    </Typography>
    <Typography mt={3}>
      14. <b>Fraud.</b> The Issuer reserves the right to refuse to honor a Gift
      Card if the Issuer suspects that a Gift Card was obtained fraudulently.
      Gift Cards are void if copied (other than for the purpose of making a
      back-up of your own Gift Card details), resold or otherwise obtained or
      used in violation of these terms. Further, use of Gift Cards for
      unauthorized advertising, marketing or other promotional purposes is
      strictly prohibited.
    </Typography>

    <Stack>
      <Typography mt={3}>
        15. <b>BINDING ARBITRATION AND CLASS ACTION WAIVER.</b> Please read this
        Section 15 carefully. It affects legal rights that you may otherwise
        have and requires individual, final, and binding arbitration of most
        disputes instead of resolution in court.
      </Typography>
      <Stack pl={2} gap={1}>
        <Typography>
          a. Arbitration is the submission of a dispute to a neutral arbitrator,
          instead of a judge or jury, for a final and binding decision, known as
          an “award.” During an arbitration, each party has an opportunity to
          present evidence to the arbitrator in writing or through witnesses,
          but arbitration provides for more limited discovery than in court, and
          the arbitrator’s award is subject to limited review by the courts. An
          arbitrator may award only the same damages and relief that a court
          could award under the law and must honor the terms and conditions of
          this Agreement.
        </Typography>
        <Typography>
          b. You and the Issuer agree that any dispute, claim or controversy,
          whether at law or equity, arising out of or relating to your use of
          the Gift Cards or this Agreement, including but not limited to the
          interpretation, applicability or enforceability of these terms or the
          formation of this Agreement, or the arbitrability of any dispute
          (“Dispute”), shall be resolved in its entirety by individual binding
          arbitration. You and the Issuer agree that any arbitration under this
          Agreement will take place on an individual basis and that class, mass,
          consolidated or combined actions or arbitrations or proceedings as a
          private attorney general are not permitted. You and the Issuer agree
          that the arbitrator may award declaratory or injunctive relief only in
          favor of the individual party seeking relief and only to the extent
          necessary to provide relief warranted by that party’s individual
          claim. You and the Issuer both waive, to the fullest extent allowed by
          law, any claims to recover punitive or exemplary damages. If it is
          decided that applicable law precludes enforcement of any of this
          paragraph’s limitations as to a particular claim for relief, then that
          claim (and only that claim) shall be severed from the arbitration and
          may be brought in court.
        </Typography>
        <Typography>
          c. This agreement to arbitrate shall survive termination of this
          Agreement and extends to any Disputes that you may assert against the
          Issuer’s affiliates. This Agreement evidences a transaction in
          interstate commerce, and the Federal Arbitration Act governs the
          interpretation and enforcement of this agreement to arbitrate.
        </Typography>
        <Typography>
          d. PROCEDURES FOR ASSERTING A CLAIM. In the event of a Dispute, you or
          the Issuer must first send to the other party a Notice of Dispute,
          which is a written statement that sets forth the name, address and
          contact information of the party giving the notice, the facts giving
          rise to the Dispute, and the relief requested. You must send any
          Notice of Dispute to the Issuer at 401 E. Jefferson Street, Phoenix,
          AZ 85004, Attention: Legal Department. Any Notice of Dispute will be
          sent to you at the contact address that the Issuer or its affiliate
          has for you. You and the Issuer will attempt to resolve a Dispute
          through informal negotiation within sixty (60) days from the date the
          Notice of Dispute is sent. After sixty (60) days, you or the Issuer
          may commence arbitration or proceed in small claims court. You may
          instead litigate a Dispute in small claims court if the Dispute meets
          the requirements to be heard in small claims court.
        </Typography>
        <Typography>
          e. If you and the Issuer do not resolve a Dispute by informal
          negotiation or in small claims court, the dispute shall be resolved by
          binding arbitration before a neutral arbitrator whose decision will be
          final except for a limited right of appeal under the U.S. Federal
          Arbitration Act. Unless the parties agree otherwise, any arbitration
          shall take place in the County of Maricopa, State of Arizona and shall
          be administered by and pursuant to the rules of the American
          Arbitration Association (“AAA”). To begin an arbitration proceeding,
          you must serve the Issuer’s registered agent for service of process,
          Paracorp Incorporated at 2140 S. Dupont Highway, Camden DE, 19934.
        </Typography>
        <Typography>
          f. Nothing herein shall be construed as consent by the Issuer or any
          of its affiliated entities to the jurisdiction of any court with
          regard to disputes, claims or controversies unrelated to the use of
          your Gift Cards or this Agreement.
        </Typography>
        <Typography>
          g. Limitation of Liability. TO THE MAXIMUM EXTENT PERMITTED BY
          APPLICABLE LAW, THE ISSUER AND ITS AFFILIATES MAKE NO WARRANTIES,
          EXPRESS OR IMPLIED, WITH RESPECT TO THE GIFT CARDS, INCLUDING, WITHOUT
          LIMITATION, ANY EXPRESS OR IMPLIED WARRANTY OF MERCHANTABILITY OR
          FITNESS FOR A PARTICULAR PURPOSE. IN THE EVENT THAT A CARD IS NON-
          FUNCTIONAL, YOUR SOLE REMEDY, AND THE SOLE LIABILITY OF THE ISSUER AND
          ITS AFFILIATES, SHALL BE THE REPLACEMENT OF SUCH GIFT CARDS. CERTAIN
          STATE LAWS DO NOT ALLOW LIMITATIONS ON IMPLIED WARRANTIES OR THE
          EXCLUSION OR LIMITATION OF CERTAIN DAMAGES. IF THESE LAWS APPLY TO
          YOU, SOME OR ALL OF THE ABOVE DISCLAIMERS, EXCLUSIONS OR LIMITATIONS
          MAY NOT APPLY TO YOU.
        </Typography>
      </Stack>
    </Stack>

    <Typography mt={3}>
      16. <b>Governing Law.</b> The laws of the State of Arizona, without regard
      to principles of conflict of laws, shall govern this Agreement and the use
      of your Gift Cards.
    </Typography>

    <Typography mt={3}>
      17. <b>Severability.</b> Notwithstanding anything herein to the contrary,
      if any part of this Agreement is deemed invalid or inapplicable, such
      provision shall be modified or restricted to the extent and in the manner
      necessary to render it valid, legal and enforceable. If such provision
      cannot be so modified or restricted, it shall be excised from this
      Agreement without affecting the validity, legality or enforceability of
      the remainder of this Agreement, which shall be fully enforced.
    </Typography>

    <Typography mt={3}>
      18. <b>Changes to Agreement.</b> The Issuer reserves the right to modify,
      alter, change or amend this Agreement from time to time in its discretion
      without advance notice, effective when posted on this web site. Customer’s
      use of the Gift Card after changes to this Agreement are posted shall
      constitute acceptance of any changed or additional terms. The current
      version of this Agreement is available at{" "}
      <Link
        href="https://www.mlb.com/dbacks/tickets/gift-cards"
        target={"_blank"}
        sx={{
          color: "primary.main",
          textDecoration: "none",
          fontWeight: 500,
        }}
      >
        https://www.mlb.com/dbacks/tickets/gift-cards
      </Link>
      .
    </Typography>
  </Box>
);

export const AnalyticsHeader = (props: { teamConfig: TeamConfig }) => {
  const { teamSchema } = props.teamConfig;

  return (
    <>
      <MLBAdobeScriptHeader />
      <Head>
        <script
          nonce={process.env.pixelScriptNonce}
          id={`${teamSchema}-meta-pixel-script`}
          defer
          dangerouslySetInnerHTML={{
            __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '1532627770340759');
              fbq('track', 'PageView');
              console.log('meta pixel page load event fired.');
              window.addEventListener("${constants.customEvents.eventPurchaseSuccess}", function(e) {
                try {
                  const { cartRes, checkoutRes } = e.detail;
                  fbq('track', 'Purchase', {
                    content_name: "${teamSchema}_event_purchase", 
                    value: String(cartRes.allInPrice), 
                    currency: 'USD', 
                    order_id: checkoutRes.orderNumber,
                  });
                  console.log('meta pixel purchase event fired.');
                } catch (error) {
                  console.log('Error in meta pixel purchase event', error); 
                }
              });

              window.addEventListener("${constants.customEvents.giftPurchaseSuccess}", function(e) {
                try {
                  const { checkoutRes } = e.detail;
                  fbq('track', 'Purchase', {
                    content_name: "${teamSchema}_gift_purchase", 
                    value: String(checkoutRes.amount), 
                    currency: 'USD', 
                    order_id: checkoutRes.orderNumber,
                  });
                  console.log('meta pixel purchase event fired.');
                } catch (error) {
                  console.log('Error in meta pixel purchase event', error); 
                }
              });
            `,
          }}
        />

        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: "none" }}
            src="https://www.facebook.com/tr?id=1532627770340759&ev=PageView&noscript=1"
          />
        </noscript>
      </Head>
    </>
  );
};
